import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { salesTrainingService } from '../services/sales-training-service';
import {
  SalesProfile,
  SalesProfileUpdate,
  SalesExample,
  CreateExampleData,
  UpdateExampleData,
  SalesExampleCategory,
} from '../types/sales-training.types';

interface SalesTrainingState {
  profile: SalesProfile | null;
  examples: SalesExample[];
  isLoading: boolean;
  error: string | null;

  fetchProfile: () => Promise<void>;
  updateProfile: (data: SalesProfileUpdate) => Promise<SalesProfile>;
  fetchExamples: (category?: SalesExampleCategory) => Promise<void>;
  createExample: (data: CreateExampleData) => Promise<SalesExample>;
  updateExample: (id: string, data: UpdateExampleData) => Promise<SalesExample>;
  deleteExample: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useSalesTrainingStore = create<SalesTrainingState>()(
  devtools(
    (set) => ({
      profile: null,
      examples: [],
      isLoading: false,
      error: null,

      fetchProfile: async () => {
        try {
          set({ isLoading: true, error: null });
          const profile = await salesTrainingService.getProfile();
          set({ profile, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al cargar perfil de ventas',
            isLoading: false,
          });
        }
      },

      updateProfile: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const profile = await salesTrainingService.updateProfile(data);
          set({ profile, isLoading: false });
          return profile;
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al actualizar perfil',
            isLoading: false,
          });
          throw error;
        }
      },

      fetchExamples: async (category?) => {
        try {
          set({ isLoading: true, error: null });
          const examples = await salesTrainingService.getExamples(category);
          set({ examples, isLoading: false });
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al cargar ejemplos',
            isLoading: false,
          });
        }
      },

      createExample: async (data) => {
        try {
          set({ isLoading: true, error: null });
          const example = await salesTrainingService.createExample(data);
          set((state) => ({
            examples: [...state.examples, example],
            isLoading: false,
          }));
          return example;
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al crear ejemplo',
            isLoading: false,
          });
          throw error;
        }
      },

      updateExample: async (id, data) => {
        try {
          set({ isLoading: true, error: null });
          const example = await salesTrainingService.updateExample(id, data);
          set((state) => ({
            examples: state.examples.map((e) => (e.id === id ? example : e)),
            isLoading: false,
          }));
          return example;
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al actualizar ejemplo',
            isLoading: false,
          });
          throw error;
        }
      },

      deleteExample: async (id) => {
        try {
          set({ isLoading: true, error: null });
          await salesTrainingService.deleteExample(id);
          set((state) => ({
            examples: state.examples.filter((e) => e.id !== id),
            isLoading: false,
          }));
        } catch (error: any) {
          set({
            error: error.response?.data?.error || 'Error al eliminar ejemplo',
            isLoading: false,
          });
          throw error;
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: 'SalesTrainingStore' }
  )
);

export default useSalesTrainingStore;
